import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { RegistrationService } from '../../services/registrationService';

import logger from '../../core/utils/logger';

// Async thunk for customer registration
export const registerUser = createAsyncThunk(
    'registration/registerUser',
    async (userData, { rejectWithValue }) => {
      try {
        const response = await RegistrationService.registerRequest(userData);
        // Expect response.data to include { message, organisationId, ... }
        return response;
      } catch (error) {
        return rejectWithValue(
          error.response?.data || 'Error during registration'
        );
      }
    }
  );
  // Async thunk for inviting user
    export const inviteUser = createAsyncThunk(
        'registration/inviteUser',
        async (invitationData, { rejectWithValue }) => {
          try {
            logger.log("[Redux - inviteUser] Starting invitation request with data:", invitationData);
            const response = await RegistrationService.inviteUserRequest(invitationData);
            logger.log("[Redux - inviteUser] Service returned response:", response);
            // Expect response.data to include { message, organisationId, ... }
            return response;
          } catch (error) {
            // Handle both API errors and service errors
            logger.error("[Redux - inviteUser] Caught error:", error);
            logger.error("[Redux - inviteUser] Error response:", error.response);
            logger.error("[Redux - inviteUser] Error message:", error.message);

            // 402 — license gate (NoLicense, Expired, LimitReached). Surface the structured payload
            // so EmployeeManagement.jsx can render an inline alert and refresh licenseStatus.
            // Always treat 402 as the gate branch, even when the body is missing/non-object —
            // defaulting to `NoLicense` so the UI still shows a sensible blocked-message.
            if (error.response?.status === 402) {
              const raw = error.response?.data;
              const body = raw && typeof raw === 'object' ? raw : {};
              const KNOWN_REASONS = ['NoLicense', 'Expired', 'LimitReached'];
              const reason = KNOWN_REASONS.includes(body.reason) ? body.reason : 'NoLicense';
              return rejectWithValue({
                httpStatus: 402,
                reason,
                currentUsers: body.currentUsers,
                maxUsers: body.maxUsers,
                validUntil: body.validUntil,
                message: body.message || 'Invite blocked by license gate',
              });
            }

            const errorMessage = error.response?.data?.message || error.message || 'Error during invitation';
            const errorCode = error.response?.data?.error || 'UNKNOWN_ERROR';

            logger.error("[Redux - inviteUser] Processed error message:", errorMessage);
            logger.error("[Redux - inviteUser] Processed error code:", errorCode);

            return rejectWithValue({
              message: errorMessage,
              error: errorCode
            });
          }
        }
        );

        const initialState = {
            registration: {
              loading: false,
              error: null,
              organisationId: null,
              message: '',
            },
            invitation: {
              loading: false,
              error: null,
              message: '',
            },
          };     

  const registrationSlice = createSlice({
    name: 'registration',
    initialState,
    reducers: {
      clearState: (state) => {
        state.registration.loading = false;
      state.registration.error = null;
      state.registration.organisationId = null;
      state.registration.message = '';
      state.invitation.loading = false;
      state.invitation.error = null;
      state.invitation.message = '';
      },
    },
    extraReducers: (builder) => {
      // Registration
      builder
      .addCase(registerUser.pending, (state) => {
        state.registration.loading = true;
        state.registration.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.registration.loading = false;
        state.registration.organisationId = action.payload?.organisationId || null;
        state.registration.message = action.payload?.message || '';
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.registration.loading = false;
        state.registration.error = action.payload?.message|| action.payload?.error || 'Error during registration';
      })
      // Invitation async thunk cases
      .addCase(inviteUser.pending, (state) => {
        state.invitation.loading = true;
        state.invitation.error = null;
      })
      .addCase(inviteUser.fulfilled, (state, action) => {
        state.invitation.loading = false;
        state.invitation.message = action.payload?.message || '';
      })
      .addCase(inviteUser.rejected, (state, action) => {
        state.invitation.loading = false;
        state.invitation.error = action.payload?.message || 'Error inviting user';
      });
    },
    });
    export const { clearState } = registrationSlice.actions;
    export default registrationSlice.reducer;


